// Emoji picker: search across the top, an icon rail on the left (Favorites, Frequently used, Server, then each group)
// and the grid: my favorites first, then frequently used (whole rows), then the server's own emoji, then every emoji by
// group. Used for reactions and the composer. A server emoji is picked as its token ("<:name:id>") plus the emoji itself.
// Favorites follow the account (favoriteEmoji.ts): Edit beside "Favorites" turns taps into starring and unstarring,
// anywhere in the picker (search results too), until Done.
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GROUPS, loadEmoji, noteEmojiUse, searchEmoji, type Emoji } from "../emoji.ts";
import { customEmojiId, emojiToken, favoriteKey, MAX_FAVORITE_EMOJI, type CustomEmoji } from "@bloom/shared";
import { refreshFavorites, toggleFavorite, useFavoriteList, useFavoritesHere } from "../favoriteEmoji.ts";
import { CustomEmojiView, searchCustom, useServerEmoji, useUsedHere } from "../customEmoji.tsx";
import { dropKeyboard } from "../mobile.ts";
import { HoldDriver, browserFlags } from "../jumboHold.ts";
import { HoldOverlay } from "./HoldOverlay.tsx";
import { getReactionEffects } from "../reactionEffects.ts";
import { playSplat, primeJumboSounds } from "../jumboSound.ts";

/** Panel size (styles.css .emoji-picker) and the gap kept to the window's edges and the button. */
const W = 360;
/** Emoji per row: the same in every section, frequently used included. */
const COLS = 8;
const H = 380;
const EDGE = 8;

/**
 * Where a picker opened from a button goes: fixed to the window (never inside the scrolling message list, so opening
 * it can't scroll or grow the list), right edges lined up with the button, below it when there's room, otherwise
 * above, and always fully on screen.
 */
function placeBy(anchor: DOMRect): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = anchor.bottom + 4;
  const above = anchor.top - 4 - H;
  const top = below + H <= vh - EDGE ? below : above >= EDGE ? above : Math.max(EDGE, vh - EDGE - H);
  const left = Math.min(Math.max(EDGE, anchor.right - W), vw - EDGE - W);
  return { position: "fixed", top, left, right: "auto" };
}

/**
 * `anchor`: the button it was opened from (reactions); the panel then floats over everything, placed by placeBy.
 * Without one it's positioned by `style` inside its parent (the composer).
 */
/**
 * Holding an emoji (#87, #714; the thresholds are JUMBO_HOLD in shared/src/jumboHold.ts): let go after 0.45 s and it is
 * a super reaction, after 1.5 s a jumbo one, and 6 s splats and sends nothing. The pick waits for the release, so the
 * hold can go on to jumbo. A hold that sent (or splatted) swallows the click that follows it.
 */
const SWALLOW_CLICK_MS = 100;

type Mode = "super" | "jumbo";

export function EmojiPicker({ onPick, onClose, style, anchor, embedded, superReactions }: {
  /**
   * superReact: picked as a super reaction (the Super switch, or a hold), when `superReactions` offers them.
   * jumbo: picked as a jumbo reaction (a hold of 1.5 s or more); it replaces superReact.
   */
  onPick: (emoji: string, custom?: CustomEmoji, superReact?: boolean, jumbo?: boolean) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  anchor?: DOMRect | null;
  /** Inside MediaPicker's tabs: it places, closes and sheets the panel. */
  embedded?: boolean;
  /** Picking a reaction: offer the Super switch and the long press (#87). */
  superReactions?: boolean;
}) {
  const server = useServerEmoji();
  const [list, setList] = useState<Emoji[] | null>(null);
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadEmoji().then(setList);
    // Favorites starred on another device since this one last asked.
    void refreshFavorites();
    if (embedded) return;
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    // Next tick, so the click that opened the picker doesn't close it.
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // On when the picker opens (Evan, #87): a reaction is super unless switched off. Reaction effects in Settings turns them all off.
  const [superOn, setSuperOn] = useState(true);
  // Starring favorites: taps star and unstar instead of picking.
  const [editing, setEditing] = useState(false);
  /** A tap that would have starred one more when the list is full. */
  const [full, setFull] = useState(false);
  const star = (u: string) => setFull(!toggleFavorite(u));
  const pick = (u: string, mode: Mode | undefined = superOn ? "super" : undefined) => {
    if (editing) return star(u);
    noteEmojiUse(u);
    if (superReactions && mode === "jumbo") onPick(u, undefined, false, true);
    else onPick(u, undefined, superReactions && mode === "super");
  };
  const pickCustom = (e: CustomEmoji, mode: Mode | undefined = superOn ? "super" : undefined) => {
    if (editing) return star(emojiToken(e));
    noteEmojiUse(emojiToken(e));
    if (superReactions && mode === "jumbo") onPick(emojiToken(e), e, false, true);
    else onPick(emojiToken(e), e, superReactions && mode === "super");
  };
  // The hold: one driver for the picker (HoldDriver runs the shared state machine on a clock and a frame loop).
  // It sends when the pointer is let go; a hold that sent or splatted swallows the click that follows it.
  const swallow = useRef(false);
  const holding = useRef<{ u: string; custom?: CustomEmoji } | null>(null);
  const latest = useRef({ pick, pickCustom });
  latest.current = { pick, pickCustom };
  const driver = useMemo(
    () =>
      HoldDriver.browser({
        flags: browserFlags(getReactionEffects),
        onSplat: playSplat,
        onSend: (_target, send) => {
          const h = holding.current;
          if (!h || send === "normal") return; // a tap is the click's job
          if (h.custom) latest.current.pickCustom(h.custom, send);
          else latest.current.pick(h.u, send);
        },
      }),
    [],
  );
  useEffect(() => () => driver.cancel(), [driver]);
  const pressable = (u: string, custom?: CustomEmoji) =>
    superReactions && !editing
      ? {
          onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            swallow.current = false;
            primeJumboSounds();
            holding.current = { u, custom };
            driver.press({ el: e.currentTarget, emoji: u, touch: e.pointerType !== "mouse" });
          },
          // A finger that has held may drift: it has left only past the slop (shared/src/jumboHold.ts).
          onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
            driver.moved(e.clientX, e.clientY);
          },
          onPointerUp: (e: React.PointerEvent<HTMLElement>) => {
            // A touch keeps its pointer after it leaves the button: let go outside it is a leave.
            if (!driver.moved(e.clientX, e.clientY)) return;
            const was = driver.release();
            if (was === "none" || was === "normal") return;
            swallow.current = true;
            setTimeout(() => (swallow.current = false), SWALLOW_CLICK_MS);
          },
          onPointerLeave: () => driver.leave(),
          onPointerCancel: () => driver.cancel(),
          onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        }
      : {};
  /** The click that follows a hold that sent or splatted is not a pick. */
  const held = () => {
    const was = swallow.current;
    swallow.current = false;
    return was;
  };
  // Every match, best first, to scroll through (the ":" menu in the composer is the one that shows a few).
  const results = useMemo(() => (list && q.trim() ? searchEmoji(list, q.trim(), Infinity) : null), [list, q]);
  const customResults = useMemo(() => (q.trim() ? searchCustom(server.emojis, q.trim(), Infinity) : null), [server.emojis, q]);
  /** Frequently used here: Unicode and this server's own emoji, most used first. */
  const used = useUsedHere("frequent", COLS * 2);
  // Whole rows only, so it lines up with the sections under it (the defaults always fill the first).
  const frequent = used.slice(0, Math.max(COLS, Math.floor(used.length / COLS) * COLS));
  const { shown: favorites, hidden: hiddenFavorites } = useFavoritesHere(server.byId);
  const favoriteCount = useFavoriteList().length;
  const starred = useMemo(() => new Set(favorites.map(favoriteKey)), [favorites]);
  /** ":shortcode:" for an emoji, by its key (with or without U+FE0F), for the cells that only have the emoji. */
  const names = useMemo(() => new Map((list ?? []).map((e) => [favoriteKey(e.unicode), `:${e.shortcodes[0] ?? e.label}:`])), [list]);
  const nameOf = (u: string) => names.get(favoriteKey(u));
  const [active, setActive] = useState("favorites");
  /** While starring: whether this one is a favorite, for its star and for screen readers. */
  const starState = (u: string) => (editing ? { "aria-pressed": starred.has(favoriteKey(u)), "data-starred": starred.has(favoriteKey(u)) || undefined } : {});
  const customCell = (e: CustomEmoji) => (
    <button key={e.id} type="button" className="emoji-cell" {...pressable(emojiToken(e), e)} {...starState(emojiToken(e))} onClick={() => !held() && pickCustom(e)} title={`:${e.name}:`} aria-label={`:${e.name}:`}>
      <CustomEmojiView id={e.id} name={e.name} className="cemoji cemoji-cell" />
    </button>
  );
  const byGroup = useMemo(() => {
    const m = new Map<number, Emoji[]>();
    for (const e of list ?? []) (m.get(e.group) ?? m.set(e.group, []).get(e.group)!).push(e);
    return m;
  }, [list]);

  const cell = (u: string, label?: string) => (
    <button key={u} type="button" className="emoji-cell" {...pressable(u)} {...starState(u)} onClick={() => !held() && pick(u)} title={label} aria-label={label ?? u}>
      {u}
    </button>
  );

  /** "Favorites" and its Edit / Done; while starring it also heads search results, so Done stays in reach. */
  const favLabel = (
    <div className="emoji-group-label emoji-fav-label">
      <span>{editing ? `Favorites · ${favoriteCount} of ${MAX_FAVORITE_EMOJI}` : "Favorites"}</span>
      <button type="button" className="emoji-fav-edit" aria-label={editing ? "Done starring favorites" : "Edit favorites"} onClick={() => { setEditing(!editing); setFull(false); }}>{editing ? "Done" : "Edit"}</button>
    </div>
  );
  /** While starring: why a tap did nothing, and the favorites this server can't show. */
  const favNotes = editing && (full || hiddenFavorites > 0) && (
    <div className="emoji-fav-hint" role="status">
      {full && `Favorites are full (${MAX_FAVORITE_EMOJI}). Unstar one first. `}
      {hiddenFavorites > 0 && `${hiddenFavorites} ${hiddenFavorites === 1 ? "favorite is" : "favorites are"} another server's emoji or deleted, so not shown here.`}
    </div>
  );

  const sheet = isNarrow();
  const search = useRef<HTMLInputElement>(null);
  // Focus the search without scrolling anything to bring it into view.
  useLayoutEffect(() => {
    if (!sheet) search.current?.focus({ preventScroll: true });
  }, []);
  const panel = (
    <div className={`emoji-picker${sheet ? " in-sheet" : ""}${embedded ? " embedded" : ""}${editing ? " starring" : ""}`} ref={box} style={sheet || embedded ? undefined : anchor ? placeBy(anchor) : style} role={embedded ? "tabpanel" : "dialog"} aria-label="Emoji picker">
      <div className="emoji-search">
        <label htmlFor="emoji-q" className="sr-only">Search emoji</label>
        <input
          id="emoji-q"
          ref={search}
          className="input"
          placeholder="Search emoji"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && customResults?.[0]) {
              e.preventDefault();
              pickCustom(customResults[0]);
            } else if (e.key === "Enter" && results?.[0]) {
              e.preventDefault();
              pick(results[0].unicode);
            }
          }}
        />
        {superReactions && (
          <label className="emoji-super" title="Super reaction: zoom, spin or poof in. Or hold an emoji.">
            <input type="checkbox" role="switch" checked={superOn} onChange={(e) => setSuperOn(e.target.checked)} />
            <span>Super</span>
          </label>
        )}
      </div>
      <div className="emoji-body">
      {!q && (
        <nav className="emoji-rail" aria-label="Emoji sections">
          {[
            { id: "favorites", label: "Favorites", icon: <StarIcon /> },
            { id: "frequent", label: "Frequently used", icon: <ClockIcon /> },
            ...(server.emojis.length ? [{ id: "server", label: "Server", icon: <CustomEmojiView id={server.emojis[0]!.id} name={server.emojis[0]!.name} className="cemoji cemoji-tab" /> }] : []),
            ...GROUPS.map((g) => ({ id: String(g.id), label: g.label, icon: <>{g.icon}</> })),
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={`emoji-rail-item${active === t.id ? " on" : ""}`}
              title={t.label}
              aria-label={t.label}
              aria-current={active === t.id ? "true" : undefined}
              onClick={() => {
                const wrap = grid.current;
                const at = wrap?.querySelector<HTMLElement>(`[data-group="${t.id}"]`);
                if (wrap && at) wrap.scrollTo({ top: at.offsetTop - wrap.offsetTop - 4 });
                setActive(t.id);
              }}
            >
              {t.icon}
            </button>
          ))}
        </nav>
      )}
      <div
        className="emoji-grid-wrap sheet-scroll"
        ref={grid}
        onTouchStart={dropKeyboard}
        onScroll={(e) => {
          // The rail follows the section at the top of the grid.
          const wrap = e.currentTarget;
          let current = "favorites";
          for (const el of wrap.querySelectorAll<HTMLElement>("[data-group]")) {
            if (el.offsetTop - wrap.offsetTop <= wrap.scrollTop + 8) current = el.dataset.group!;
          }
          if (current !== active) setActive(current);
        }}
      >
        {!list && <div className="muted" style={{ padding: 12, fontSize: 13 }}>Loading emoji…</div>}
        {results && editing && <>{favLabel}{favNotes}</>}
        {customResults && customResults.length > 0 && (
          <>
            <div className="emoji-group-label">Server</div>
            <div className="emoji-grid">{customResults.map(customCell)}</div>
          </>
        )}
        {results && (results.length || customResults?.length ? <div className="emoji-grid">{results.map((e) => cell(e.unicode, `:${e.shortcodes[0] ?? e.label}:`))}</div> : <div className="muted" style={{ padding: 12, fontSize: 13 }}>No emoji match “{q}”.</div>)}
        {!results && list && (
          <>
            <div data-group="favorites">
              {favLabel}
              {favNotes}
              {favorites.length > 0 ? (
                <div className="emoji-grid">{favorites.map((u) => {
                  const id = customEmojiId(u);
                  const e = id ? server.byId.get(id) : undefined;
                  return e ? customCell(e) : cell(u, nameOf(u));
                })}</div>
              ) : (
                <div className="emoji-fav-hint">{editing ? "Tap emoji to star them. They follow you to every device." : "Tap Edit, then the emoji you use most."}</div>
              )}
            </div>
            <div data-group="frequent">
              <div className="emoji-group-label">Frequently used</div>
              <div className="emoji-grid">{frequent.map((u) => {
                const id = customEmojiId(u);
                const e = id ? server.byId.get(id) : undefined;
                return e ? customCell(e) : cell(u, nameOf(u));
              })}</div>
            </div>
            {server.emojis.length > 0 && (
              <div data-group="server">
                <div className="emoji-group-label">Server</div>
                <div className="emoji-grid">{server.emojis.map(customCell)}</div>
              </div>
            )}
            {GROUPS.map((g) => (
              <div key={g.id} data-group={g.id}>
                <div className="emoji-group-label">{g.label}</div>
                <div className="emoji-grid">{(byGroup.get(g.id) ?? []).map((e) => cell(e.unicode, `:${e.shortcodes[0] ?? e.label}:`))}</div>
              </div>
            ))}
          </>
        )}
      </div>
      </div>
      {superReactions && <HoldOverlay driver={driver} />}
    </div>
  );
  if (embedded) return panel;
  // Phones: a bottom sheet instead of a floating panel.
  return sheet ? (
    <Sheet label="Emoji picker" onClose={onClose}>
      {panel}
    </Sheet>
  ) : anchor ? (
    createPortal(panel, document.body)
  ) : (
    panel
  );
}

function StarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
